import { useState } from "react";
import { api } from "../lib/utils";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Trash2,
  Download,
  Settings,
  AlertTriangle,
  Loader2,
  MoreVertical,
  CheckSquare,
  XSquare,
  FileJson,
} from "lucide-react";

const ADMIN_PASSWORD = "CredentiaAdmin2024!";

/**
 * Reusable Maintenance Panel for Entity Pages
 * 
 * @param {string} entityType - Type of entity (competencies, institutions, ipcbs, awards)
 * @param {string} entityLabel - Display label (e.g., "Competencies", "Institutions")
 * @param {array} items - Array of items with id field for selection
 * @param {string} idField - Name of the ID field (e.g., "competency_id", "institution_id")
 * @param {function} onRefresh - Callback to refresh data after operations
 * @param {function} getItemLabel - Function to get display label for an item
 */
export default function MaintenancePanel({ 
  entityType, 
  entityLabel, 
  items = [], 
  idField,
  onRefresh,
  getItemLabel = (item) => item.name || item.title || item[idField]
}) {
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleSelectAll = () => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map(item => item[idField]));
    }
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleAction = (action) => {
    setCurrentAction(action);
    setAdminPassword("");
    setConfirmDialogOpen(true);
  };

  const executeAction = async () => {
    if (adminPassword !== ADMIN_PASSWORD) {
      toast.error("Invalid admin password");
      return;
    }

    setProcessing(true);
    try {
      switch (currentAction?.type) {
        case "delete_selected":
          // Delete selected items one by one
          let deleteCount = 0;
          for (const itemId of selectedItems) {
            try {
              await api.delete(`/${entityType}/${itemId}`);
              deleteCount++;
            } catch (e) {
              console.error(`Failed to delete ${itemId}:`, e);
            }
          }
          toast.success(`Deleted ${deleteCount} ${entityLabel.toLowerCase()}`);
          setSelectedItems([]);
          setSelectMode(false);
          break;

        case "purge_all":
          // Delete all items
          let purgeCount = 0;
          for (const item of items) {
            try {
              await api.delete(`/${entityType}/${item[idField]}`);
              purgeCount++;
            } catch (e) {
              console.error(`Failed to delete ${item[idField]}:`, e);
            }
          }
          toast.success(`Purged ${purgeCount} ${entityLabel.toLowerCase()}`);
          break;

        case "export":
          // Export to JSON
          const dataToExport = selectedItems.length > 0 
            ? items.filter(item => selectedItems.includes(item[idField]))
            : items;
          
          const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${entityType}_export_${new Date().toISOString().split('T')[0]}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          toast.success(`Exported ${dataToExport.length} ${entityLabel.toLowerCase()} to JSON`);
          break;

        default:
          break;
      }

      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Operation failed`);
    } finally {
      setProcessing(false);
      setConfirmDialogOpen(false);
      setCurrentAction(null);
    }
  };

  return (
    <>
      {/* Maintenance Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" data-testid="maintenance-menu-btn">
            <Settings className="w-4 h-4 mr-2" />
            Maintenance
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem 
            onClick={() => setSelectMode(!selectMode)}
            data-testid="toggle-select-mode"
          >
            {selectMode ? (
              <>
                <XSquare className="w-4 h-4 mr-2" />
                Exit Selection Mode
              </>
            ) : (
              <>
                <CheckSquare className="w-4 h-4 mr-2" />
                Select Items
              </>
            )}
          </DropdownMenuItem>
          
          {selectMode && selectedItems.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => handleAction({ 
                  type: "delete_selected", 
                  label: `Delete ${selectedItems.length} Selected`,
                  warning: `This will permanently delete ${selectedItems.length} ${entityLabel.toLowerCase()}.`
                })}
                className="text-red-600"
                data-testid="delete-selected-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Selected ({selectedItems.length})
              </DropdownMenuItem>
            </>
          )}
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem 
            onClick={() => handleAction({ 
              type: "export", 
              label: selectedItems.length > 0 ? `Export ${selectedItems.length} Selected` : `Export All ${entityLabel}`,
              warning: `This will download ${selectedItems.length > 0 ? selectedItems.length : items.length} ${entityLabel.toLowerCase()} as JSON.`
            })}
            data-testid="export-btn"
          >
            <FileJson className="w-4 h-4 mr-2" />
            {selectedItems.length > 0 ? `Export Selected (${selectedItems.length})` : "Export All to JSON"}
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem 
            onClick={() => handleAction({ 
              type: "purge_all", 
              label: `Purge All ${entityLabel}`,
              warning: `This will PERMANENTLY DELETE all ${items.length} ${entityLabel.toLowerCase()}. This action cannot be undone!`
            })}
            className="text-red-600"
            data-testid="purge-all-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Purge All ({items.length})
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Selection Mode Banner */}
      {selectMode && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Badge variant="outline" className="bg-white">
                  {selectedItems.length} of {items.length} selected
                </Badge>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleSelectAll}
                  data-testid="select-all-btn"
                >
                  {selectedItems.length === items.length ? "Deselect All" : "Select All"}
                </Button>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setSelectMode(false);
                  setSelectedItems([]);
                }}
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              {currentAction?.label}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {currentAction?.warning}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Admin Password</Label>
              <Input
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                placeholder="Enter admin password to confirm"
                data-testid="admin-password-input"
              />
              <p className="text-xs text-slate-500">
                Required for destructive operations
              </p>
            </div>
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={executeAction}
              disabled={processing || !adminPassword}
              className={currentAction?.type === "export" ? "" : "bg-red-600 hover:bg-red-700"}
              data-testid="confirm-action-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                currentAction?.label
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Export selectMode and handlers for parent to use */}
      {selectMode && (
        <style>{`
          [data-selectable="true"] {
            cursor: pointer;
          }
          [data-selectable="true"]:hover {
            background-color: rgba(59, 130, 246, 0.05);
          }
        `}</style>
      )}
    </>
  );
}

// Export helper for checking if item is selected
export const isItemSelected = (selectedItems, itemId) => selectedItems.includes(itemId);

// Export the selection state hook for parent components
export const useMaintenanceSelection = () => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectMode, setSelectMode] = useState(false);

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAll = (items, idField) => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map(item => item[idField]));
    }
  };

  const clearSelection = () => {
    setSelectedItems([]);
    setSelectMode(false);
  };

  return {
    selectedItems,
    setSelectedItems,
    selectMode,
    setSelectMode,
    handleSelectItem,
    handleSelectAll,
    clearSelection,
    isSelected: (itemId) => selectedItems.includes(itemId)
  };
};
